import { Dispatch, SetStateAction } from 'react'

import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import { useCallMutation } from '@/common/api/client'
import {
  apiDefaultFetcher,
  apiDeleteFetcher,
  apiPostFetcher,
  apiPutFetcher,
  fetchRest
} from '@/common/api/fetcher'
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/common/api/pagination'
import {
  ArticlesApiPath,
  BatchDiagnoseArticlesResponse,
  BatchDiagnoseArticlesVars,
  CreateArticleResponse,
  CreateArticleVars,
  CreateArticleVersionResponse,
  CreateArticleVersionVars,
  DeleteArticleResponse,
  DeleteArticleVars,
  DuplicateArticleResponse,
  DuplicateArticleVars,
  GetArticleDetailResponse,
  GetArticleDetailVars,
  GetArticleVersionDetailResponse,
  GetArticleVersionDetailVars,
  ListArticlesResponse,
  ListArticlesVars,
  UpdateArticleResponse,
  UpdateArticleVars,
  UploadArticleImageResponse,
  UploadArticleImageVars
} from '@/common/api/params/articles'
import { SearchConditionBase, SearchConditionParam, useSearchCondition } from '@/common/api/search'
import { ArticleCreationSource } from '@/common/model/media/article'

// -----------------------------------------------
// 検索条件
// -----------------------------------------------

export interface ArticleSearchCondition extends SearchConditionBase {
  creationSource: ArticleCreationSource | null
  status: string | null
  siteOperatorId: number | null
}

const articleSearchConditionParam: SearchConditionParam[] = [
  { name: 'creationSource', type: 'string' },
  { name: 'status', type: 'string' },
  { name: 'siteOperatorId', type: 'number' }
]

export const useArticleSearchCondition = (): [
  ArticleSearchCondition,
  Dispatch<SetStateAction<ArticleSearchCondition>>,
  (page: number) => void
] => {
  return useSearchCondition<ArticleSearchCondition>(articleSearchConditionParam)
}

export const convertConditionToListArticlesVars = (
  siteId: number,
  condition: ArticleSearchCondition
): ListArticlesVars => ({
  site_id: siteId,
  creation_source: condition.creationSource || null,
  status: condition.status || null,
  site_operator_id: condition.siteOperatorId || null,
  page: condition.page,
  per_page: condition.perPage
})

// 記事診断結果一覧（バッチ診断はサイト全体を一括取得するため、絞り込みはクライアント側で行う）
export interface ArticleAnalysisSearchCondition extends SearchConditionBase {
  priority: string | null
  title: string | null
}

const articleAnalysisSearchConditionParam: SearchConditionParam[] = [
  { name: 'priority', type: 'string' },
  { name: 'title', type: 'string' }
]

export const ARTICLE_ANALYSIS_CONDITION_CLEAR: ArticleAnalysisSearchCondition = {
  page: DEFAULT_PAGE,
  perPage: DEFAULT_PER_PAGE,
  priority: null,
  title: null
}

export const useArticleAnalysisSearchCondition = (): [
  ArticleAnalysisSearchCondition,
  Dispatch<SetStateAction<ArticleAnalysisSearchCondition>>,
  (page: number) => void
] => {
  return useSearchCondition<ArticleAnalysisSearchCondition>(articleAnalysisSearchConditionParam)
}

// -----------------------------------------------
// GET hooks
// -----------------------------------------------

export const useArticlesListApi = (vars: ListArticlesVars) => {
  return useSWR<ListArticlesResponse, Response, [string, ListArticlesVars]>(
    [ArticlesApiPath.LIST_ARTICLES, vars],
    apiDefaultFetcher
  )
}

export const useArticleDetailApi = (vars: GetArticleDetailVars | null) => {
  return useSWR<GetArticleDetailResponse, Response, [string, GetArticleDetailVars] | null>(
    vars ? [ArticlesApiPath.GET_ARTICLE_DETAIL, vars] : null,
    apiDefaultFetcher
  )
}

export const useArticleVersionDetailApi = (vars: GetArticleVersionDetailVars | null) => {
  return useSWR<
    GetArticleVersionDetailResponse,
    Response,
    [string, GetArticleVersionDetailVars] | null
  >(vars ? [ArticlesApiPath.GET_ARTICLE_VERSION_DETAIL, vars] : null, apiDefaultFetcher)
}

export const useBatchDiagnoseArticlesApi = (vars: BatchDiagnoseArticlesVars) => {
  return useSWR<BatchDiagnoseArticlesResponse, Response, [string, BatchDiagnoseArticlesVars]>(
    [ArticlesApiPath.BATCH_DIAGNOSE_ARTICLES, vars],
    apiDefaultFetcher
  )
}

// -----------------------------------------------
// Mutation hooks
// -----------------------------------------------

export const useCreateArticleMutation = (options?: {
  onSuccess?: (response: CreateArticleResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<CreateArticleResponse, CreateArticleVars>(
    ArticlesApiPath.CREATE_ARTICLE,
    { ...options },
    apiPostFetcher
  )
}

export const useCreateArticleVersionMutation = (options?: {
  onSuccess?: (response: CreateArticleVersionResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<CreateArticleVersionResponse, CreateArticleVersionVars>(
    ArticlesApiPath.CREATE_ARTICLE_VERSION,
    { ...options },
    apiPostFetcher
  )
}

export const useUpdateArticleMutation = (options?: {
  onSuccess?: (response: UpdateArticleResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<UpdateArticleResponse, UpdateArticleVars>(
    ArticlesApiPath.UPDATE_ARTICLE,
    { ...options },
    apiPutFetcher
  )
}

export const useDuplicateArticleMutation = (options?: {
  onSuccess?: (response: DuplicateArticleResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<DuplicateArticleResponse, DuplicateArticleVars>(
    ArticlesApiPath.DUPLICATE_ARTICLE,
    { ...options },
    apiPostFetcher
  )
}

export const useDeleteArticleMutation = (options?: {
  onSuccess?: (response: DeleteArticleResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<DeleteArticleResponse, DeleteArticleVars>(
    ArticlesApiPath.DELETE_ARTICLE,
    { ...options },
    apiDeleteFetcher
  )
}

const uploadArticleImageFetcher = async (
  path: string,
  { arg }: { arg: UploadArticleImageVars }
): Promise<UploadArticleImageResponse> => {
  const resolvedPath = path.replace('{site_id}', String(arg.site_id))
  const formData = new FormData()
  formData.append('file', arg.file)
  return fetchRest<UploadArticleImageResponse>(resolvedPath, {
    method: 'POST',
    body: formData
  })
}

export const useUploadArticleImageMutation = () => {
  return useSWRMutation<UploadArticleImageResponse, Response, string, UploadArticleImageVars>(
    ArticlesApiPath.UPLOAD_ARTICLE_IMAGE,
    uploadArticleImageFetcher
  )
}
